import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, Send, X } from "react-feather";
import { Modal, ModalBody, ModalHeader, ModalFooter, Input, Button } from "reactstrap";
import api from "../api";
import Botservice from "../Service/Botservice";
import websocket_webservice from "../Service/websocket_webservice";
import {
    ensureBotRuntimeAuth,
    refreshBotRuntimeToken,
    loginToBotRuntime,
    clearBotTokens,
} from "../Service/botRuntimeService";

interface HumanHelpProps { }

interface WebSocketMessage {
    type?: string;
    question?: string;
    message?: string;
    request_id?: string;
    [key: string]: any;
}

// Base reconnect delay; grows exponentially up to MAX_RECONNECT_DELAY.
const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_DELAY = 30000;
// Application-level heartbeat. The server keeps one socket per user_id, so a
// half-open ("zombie") socket - readyState OPEN in the browser while the server
// has already dropped it - silently stops human_help delivery until a refresh.
// Pings let us detect that and force a reconnect on our own.
const HEARTBEAT_INTERVAL = 25000;
const PONG_TIMEOUT = 60000;
// A handshake that never completes must not block reconnects: a socket parked in
// CONNECTING is given up on after this long and retried.
const CONNECT_TIMEOUT = 10000;
const UNMOUNT_REASON = "Component unmounting";

export const getAuthInfo = () => {
    try {
        const id = localStorage.getItem('id') || '';
        const login = localStorage.getItem('login');
        const isAuthenticated = !!id && (login === 'true' || login === '"true"' || login === '1');
        const userName = localStorage.getItem('UserName') || '';
        return { userId: id, isAuthenticated, userName };
    } catch {
        return { userId: '', isAuthenticated: false, userName: '' };
    }
};

export const checkUserBotManagerPermission = async (userId: string): Promise<boolean> => {
    if (!userId) return false;
    try {
        const response = await api.fetch("api/Get_permission", {
            method: "POST",
            body: JSON.stringify({ data: userId }),
        }, 3); // read-only POST: retry on transient network errors (e.g. after tab duplication)
        if (!response.ok) return false;
        const jsonData = await response.json();
        if (Array.isArray(jsonData) && Array.isArray(jsonData[0]?.Permission)) {
            return jsonData[0].Permission.some(
                (item: any) => item.MenuKey === 'BOTMANAGER'
            );
        }
        return false;
    } catch (error) {
        console.error("[HumanHelp] Error checking BOTMANAGER permission:", error);
        return false;
    }
};

const HumanHelp: React.FC<HumanHelpProps> = () => {
    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [inputValue, setInputValue] = useState<string>("");
    const [requestId, setRequestId] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [hasBotManagerPermission, setHasBotManagerPermission] = useState<boolean>(false);

    const hasBotManagerPermissionRef = useRef<boolean>(false);
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttemptsRef = useRef<number>(0);
    // Set before the awaited token fetch so two overlapping connect() calls
    // cannot both create a socket for the same user_id.
    const isConnectingRef = useRef<boolean>(false);
    const connectStartedAtRef = useRef<number>(0);
    const connectWatchdogRef = useRef<NodeJS.Timeout | null>(null);
    const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastPongAtRef = useRef<number>(0);
    const pongSeenRef = useRef<boolean>(false);
    const socketSeqRef = useRef<number>(0);
    // Raised when a socket closed without ever opening (handshake rejected),
    // which usually means the stored bot access token is no longer valid.
    const forceTokenRefreshRef = useRef<boolean>(false);
    const unmountedRef = useRef<boolean>(false);

    const connectRef = useRef<() => void>(() => { });
    const disconnectRef = useRef<(reason?: string) => void>(() => { });
    const broadcastRef = useRef<BroadcastChannel | null>(null);

    // Setup BroadcastChannel for cross-tab modal synchronization
    useEffect(() => {
        if (typeof window === "undefined" || !("BroadcastChannel" in window)) {
            return;
        }

        const channel = new BroadcastChannel("human_help_sync_channel");
        broadcastRef.current = channel;

        channel.onmessage = (event) => {
            const data = event.data;
            if (!data || typeof data !== "object") return;

            if (data.type === "SHOW_HUMAN_HELP") {
                if (!hasBotManagerPermissionRef.current) return;
                setMessage(data.message || "");
                setRequestId(data.requestId || "");
                setModalOpen(true);
            } else if (data.type === "CLOSE_HUMAN_HELP") {
                setModalOpen(false);
                setInputValue("");
                setMessage("");
                setRequestId("");
            } else if (data.type === "SUBMIT_HUMAN_HELP") {
                // If this tab has the active WebSocket connection, send the response on behalf of the user
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    try {
                        const { userId } = getAuthInfo();
                        const response = {
                            type: "human_help_response",
                            request_id: data.requestId,
                            value: data.value,
                            user_id: userId,
                        };
                        wsRef.current.send(JSON.stringify(response));
                    } catch (err) {
                        console.error("Error sending proxy response over WebSocket:", err);
                    }
                }
                setModalOpen(false);
                setInputValue("");
                setMessage("");
                setRequestId("");
            }
        };

        return () => {
            channel.close();
            broadcastRef.current = null;
        };
    }, []);

    // Detach handlers before closing so the dying socket cannot schedule a
    // reconnect or clobber state belonging to its replacement.
    const detachAndClose = useCallback((ws: WebSocket, code: number, reason: string) => {
        try {
            ws.onopen = null;
            ws.onmessage = null;
            ws.onerror = null;
            ws.onclose = null;
            if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                ws.close(code, reason);
            }
        } catch (error) {
            console.error("[HumanHelp] Error closing previous WebSocket:", error);
        }
    }, []);

    const stopHeartbeat = useCallback(() => {
        if (heartbeatTimerRef.current) {
            clearInterval(heartbeatTimerRef.current);
            heartbeatTimerRef.current = null;
        }
    }, []);

    const clearConnectWatchdog = useCallback(() => {
        if (connectWatchdogRef.current) {
            clearTimeout(connectWatchdogRef.current);
            connectWatchdogRef.current = null;
        }
    }, []);

    const disconnect = useCallback((reason = "User logged out") => {
        stopHeartbeat();
        clearConnectWatchdog();
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        if (wsRef.current) {
            detachAndClose(wsRef.current, 1000, reason);
            wsRef.current = null;
        }
        isConnectingRef.current = false;
    }, [detachAndClose, stopHeartbeat, clearConnectWatchdog]);

    useEffect(() => {
        disconnectRef.current = disconnect;
    }, [disconnect]);

    const evaluatePermission = useCallback(async () => {
        const { userId, isAuthenticated } = getAuthInfo();
        if (!isAuthenticated || !userId) {
            hasBotManagerPermissionRef.current = false;
            setHasBotManagerPermission(false);
            if (wsRef.current) {
                disconnect("User not authenticated");
            }
            return false;
        }

        const hasPerm = await checkUserBotManagerPermission(userId);
        hasBotManagerPermissionRef.current = hasPerm;
        setHasBotManagerPermission(hasPerm);

        if (!hasPerm) {
            setModalOpen(false);
            if (wsRef.current) {
                disconnect("No BOTMANAGER permission");
            }
        }
        return hasPerm;
    }, [disconnect]);

    // Real-time permission changes listener via WebSocket
    useEffect(() => {
        let permSocket: WebSocket | null = null;
        try {
            permSocket = new WebSocket(`${websocket_webservice}ws/Permission/`);
            permSocket.onmessage = () => {
                evaluatePermission();
            };
        } catch (err) {
            console.error("[HumanHelp] Error setting up Permission WebSocket:", err);
        }

        return () => {
            if (permSocket && permSocket.readyState === WebSocket.OPEN) {
                permSocket.close();
            }
        };
    }, [evaluatePermission]);

    const startHeartbeat = useCallback((ws: WebSocket, seq: number) => {
        stopHeartbeat();
        lastPongAtRef.current = Date.now();

        heartbeatTimerRef.current = setInterval(() => {
            if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) {
                stopHeartbeat();
                return;
            }

            const { userId } = getAuthInfo();
            if (!userId) {
                disconnect("No active user for heartbeat");
                return;
            }

            try {
                ws.send(JSON.stringify({ type: "ping", user_id: userId }));
            } catch (error) {
                console.error(`[HumanHelp #${seq}] Heartbeat send failed:`, error);
            }

            // Only act on a missing pong once the server has proven it answers
            // pings at least once - otherwise an older backend that ignores
            // them would put us in a permanent reconnect loop.
            if (pongSeenRef.current && Date.now() - lastPongAtRef.current > PONG_TIMEOUT) {
                console.warn(`[HumanHelp #${seq}] No pong for >${PONG_TIMEOUT}ms, forcing reconnect`);
                stopHeartbeat();
                try {
                    ws.close(4000, "Heartbeat timeout");
                } catch (error) {
                    console.error(`[HumanHelp #${seq}] Error closing stale socket:`, error);
                }
            }
        }, HEARTBEAT_INTERVAL);
    }, [stopHeartbeat, disconnect]);

    const scheduleReconnect = useCallback(() => {
        if (unmountedRef.current) {
            return;
        }

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        const attempt = Math.max(0, reconnectAttemptsRef.current - 1);
        const delay = Math.min(RECONNECT_DELAY * Math.pow(2, attempt), MAX_RECONNECT_DELAY);

        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            if (connectRef.current) {
                connectRef.current();
            }
        }, delay);
    }, []);

    // Refresh the bot runtime token, falling back to a fresh login.
    const renewBotToken = useCallback(async (id: string) => {
        try {
            return await refreshBotRuntimeToken();
        } catch (error) {
            console.warn("[HumanHelp] Token refresh failed, re-logging in:", error);
            clearBotTokens();
            const loginResponse = await loginToBotRuntime(id);
            return loginResponse.access_token;
        }
    }, []);

    const connect = useCallback(() => {
        const { userId, isAuthenticated } = getAuthInfo();
        if (!userId || !isAuthenticated || !hasBotManagerPermissionRef.current || unmountedRef.current) {
            return;
        }

        const attemptAge = Date.now() - connectStartedAtRef.current;

        // A stalled attempt must never block future ones forever.
        if (isConnectingRef.current) {
            if (attemptAge < CONNECT_TIMEOUT) {
                return;
            }
            console.warn("[HumanHelp] Previous connect attempt stalled, starting a new one");
            isConnectingRef.current = false;
        }

        const existing = wsRef.current;
        if (existing && existing.readyState === WebSocket.OPEN) {
            return;
        }
        // Only respect a CONNECTING socket while its handshake is still plausibly alive;
        // otherwise fall through and replace it.
        if (existing && existing.readyState === WebSocket.CONNECTING && attemptAge < CONNECT_TIMEOUT) {
            return;
        }

        isConnectingRef.current = true;
        connectStartedAtRef.current = Date.now();

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        const seq = ++socketSeqRef.current;

        (async () => {
            try {
                // Never leave an orphaned socket behind: the server keys its
                // registry by user_id, so a late close from an orphan can
                // unregister the socket that replaced it.
                if (existing) {
                    detachAndClose(existing, 1000, "Replaced by new connection");
                    if (wsRef.current === existing) {
                        wsRef.current = null;
                    }
                    stopHeartbeat();
                }

                let accessToken: string;
                if (forceTokenRefreshRef.current) {
                    forceTokenRefreshRef.current = false;
                    accessToken = await renewBotToken(userId);
                } else {
                    accessToken = await ensureBotRuntimeAuth(userId);
                }

                if (!accessToken) {
                    console.warn("[HumanHelp] No bot access token found, retrying later.");
                    isConnectingRef.current = false;
                    reconnectAttemptsRef.current++;
                    scheduleReconnect();
                    return;
                }

                if (unmountedRef.current || !hasBotManagerPermissionRef.current) {
                    isConnectingRef.current = false;
                    return;
                }

                // Guard again in case a socket appeared during the async token fetch
                const current = wsRef.current;
                if (current && (current.readyState === WebSocket.OPEN || current.readyState === WebSocket.CONNECTING)) {
                    isConnectingRef.current = false;
                    return;
                }

                const wsBase = Botservice.replace(/^https/, 'wss').replace(/^http/, 'ws');
                const wsUrl = `${wsBase}ws/human/${userId}?token=${encodeURIComponent(accessToken)}`;
                const ws = new WebSocket(wsUrl);
                // Assigned immediately (not after onopen) so concurrent callers see it.
                wsRef.current = ws;
                let opened = false;

                // console.log(`[HumanHelp #${seq}] Connecting to ws/human/${userId}`);

                // Some handshakes never resolve (network change, unreachable host). Without
                // this the socket sits in CONNECTING forever and no reconnect is attempted.
                clearConnectWatchdog();
                connectWatchdogRef.current = setTimeout(() => {
                    connectWatchdogRef.current = null;
                    if (wsRef.current !== ws || ws.readyState !== WebSocket.CONNECTING) {
                        return;
                    }

                    console.warn(`[HumanHelp #${seq}] Handshake stalled after ${CONNECT_TIMEOUT}ms, retrying`);
                    detachAndClose(ws, 4001, "Handshake timeout");
                    wsRef.current = null;
                    isConnectingRef.current = false;
                    reconnectAttemptsRef.current++;
                    scheduleReconnect();
                }, CONNECT_TIMEOUT);

                ws.onopen = () => {
                    opened = true;
                    isConnectingRef.current = false;
                    reconnectAttemptsRef.current = 0;
                    clearConnectWatchdog();
                    console.log(`[HumanHelp #${seq}] WebSocket connected`);
                    startHeartbeat(ws, seq);
                };

                ws.onmessage = (event) => {
                    try {
                        const data: WebSocketMessage = JSON.parse(event.data);

                        if (data.type === 'pong') {
                            pongSeenRef.current = true;
                            lastPongAtRef.current = Date.now();
                            return;
                        }

                        // The server accepts the socket before rejecting a bad/expired
                        // token, so an auth failure arrives as a message, not a failed
                        // handshake. Renew the token before the next attempt.
                        if (data.type === 'error') {
                            console.warn(`[HumanHelp #${seq}] Server rejected the connection:`, data.message);
                            forceTokenRefreshRef.current = true;
                            return;
                        }

                        console.log('WebSocketMessage', data);
                        // Only show modal when type is 'human_help' and user has BOTMANAGER permission
                        if (data.type === 'human_help') {
                            if (!hasBotManagerPermissionRef.current) {
                                return;
                            }
                            const displayMessage = data.question || data.message || "";
                            if (displayMessage) {
                                setMessage(displayMessage);
                                setRequestId(data.request_id || "");
                                setModalOpen(true);

                                // Broadcast to all other open tabs (parent & duplicates)
                                if (broadcastRef.current) {
                                    broadcastRef.current.postMessage({
                                        type: "SHOW_HUMAN_HELP",
                                        message: displayMessage,
                                        requestId: data.request_id || "",
                                    });
                                }
                            } else {
                                console.warn(`[HumanHelp #${seq}] human_help received with an empty question`, data);
                            }
                        }
                    } catch (error) {
                        console.error("Error parsing message:", error);
                    }
                };

                ws.onerror = (error) => {
                    console.error(`[HumanHelp #${seq}] WebSocket error:`, error);
                };

                ws.onclose = (event) => {
                    isConnectingRef.current = false;
                    clearConnectWatchdog();

                    if (wsRef.current === ws) {
                        wsRef.current = null;
                        stopHeartbeat();
                    }

                    console.warn(
                        `[HumanHelp #${seq}] WebSocket closed (code=${event.code}, reason="${event.reason || ''}", opened=${opened})`
                    );

                    if (unmountedRef.current || event.reason === UNMOUNT_REASON) {
                        return;
                    }

                    // Closed before opening, or closed with a policy violation => the token
                    // is the usual culprit, so renew it before retrying.
                    if (!opened || event.code === 1008) {
                        forceTokenRefreshRef.current = true;
                    }

                    reconnectAttemptsRef.current++;
                    scheduleReconnect();
                };
            } catch (error) {
                console.error(`[HumanHelp #${seq}] Critical error creating WebSocket connection:`, error);
                isConnectingRef.current = false;
                reconnectAttemptsRef.current++;
                scheduleReconnect();
            }
        })();
    }, [detachAndClose, stopHeartbeat, startHeartbeat, scheduleReconnect, renewBotToken, clearConnectWatchdog]);

    useEffect(() => {
        connectRef.current = connect;
    }, [connect]);

    // Permanent background auto-connect & health check (runs regardless of initial mount state)
    useEffect(() => {
        unmountedRef.current = false;

        const checkAndConnect = async () => {
            const { userId, isAuthenticated } = getAuthInfo();
            if (!isAuthenticated || !userId) {
                hasBotManagerPermissionRef.current = false;
                setHasBotManagerPermission(false);
                if (wsRef.current) {
                    disconnect("Logged out");
                }
                return;
            }

            const hasPerm = await evaluatePermission();
            if (!hasPerm) {
                return;
            }

            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) {
                return;
            }

            // A retry is already queued - let it fire so backoff is respected.
            if (reconnectTimeoutRef.current) {
                return;
            }

            // Never race a healthy in-flight attempt (duplicate sockets for one user_id
            // are what break server-side registration), but do take over a stalled one.
            const stalled = Date.now() - connectStartedAtRef.current >= CONNECT_TIMEOUT;
            if (!stalled && (isConnectingRef.current || (ws && ws.readyState === WebSocket.CONNECTING))) {
                return;
            }

            // console.log("[HumanHelp] Connecting/reconnecting WebSocket with BOTMANAGER permission...");
            connect();
        };

        // Try connecting immediately on mount (or if already authenticated)
        checkAndConnect();

        // 5-second auto reconnect & auth health check
        const AUTO_CONNECT_INTERVAL = 5000;
        const autoConnectTimer = setInterval(checkAndConnect, AUTO_CONNECT_INTERVAL);

        // Listen for explicit login / logout / token events across tabs or local dispatch
        const handleAuthChange = () => {
            checkAndConnect();
        };

        const kick = (reason: string) => {
            const { userId, isAuthenticated } = getAuthInfo();
            if (!isAuthenticated || !userId || !hasBotManagerPermissionRef.current) {
                return;
            }

            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) {
                try {
                    ws.send(JSON.stringify({ type: "ping", user_id: userId }));
                } catch {
                    /* handled by reconnect path */
                }
                return;
            }

            console.log(`[HumanHelp] Reconnecting after ${reason}`);
            reconnectAttemptsRef.current = 0;
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            connectRef.current?.();
        };

        const handleOnline = () => kick("network online");
        const handleVisibility = () => {
            if (document.visibilityState === "visible") {
                kick("tab visible");
            }
        };
        const handleFocus = () => kick("window focus");

        window.addEventListener("auth_state_changed", handleAuthChange);
        window.addEventListener("storage", handleAuthChange);
        window.addEventListener("online", handleOnline);
        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            unmountedRef.current = true;
            clearInterval(autoConnectTimer);
            stopHeartbeat();
            clearConnectWatchdog();

            window.removeEventListener("auth_state_changed", handleAuthChange);
            window.removeEventListener("storage", handleAuthChange);
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("focus", handleFocus);
            document.removeEventListener("visibilitychange", handleVisibility);

            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            if (wsRef.current) {
                detachAndClose(wsRef.current, 1000, UNMOUNT_REASON);
                wsRef.current = null;
            }

            isConnectingRef.current = false;
        };
    }, [connect, disconnect, stopHeartbeat, clearConnectWatchdog, detachAndClose, evaluatePermission]);

    const handleSubmit = useCallback(() => {
        if (!inputValue.trim()) {
            return;
        }

        setIsSubmitting(true);

        const { userId } = getAuthInfo();
        const responseVal = inputValue.trim();
        const reqId = requestId;

        try {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                const response = {
                    type: 'human_help_response',
                    request_id: reqId,
                    value: responseVal,
                    user_id: userId,
                };
                wsRef.current.send(JSON.stringify(response));
            } else if (broadcastRef.current) {
                // If WS is not open in this tab, proxy submission to tab holding active WS
                broadcastRef.current.postMessage({
                    type: "SUBMIT_HUMAN_HELP",
                    value: responseVal,
                    requestId: reqId,
                });
            }

            // Broadcast close to all tabs
            if (broadcastRef.current) {
                broadcastRef.current.postMessage({ type: "CLOSE_HUMAN_HELP" });
            }

            // Close modal locally
            setModalOpen(false);
            setInputValue("");
            setMessage("");
            setRequestId("");
        } catch (error) {
            console.error("Error sending response:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [inputValue, requestId]);

    const handleClose = useCallback(() => {
        // Send a typed cancel so the backend can release the waiting run instead
        // of blocking for its full response timeout.
        const { userId } = getAuthInfo();
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            const cancelResponse = {
                type: 'human_help_cancelled',
                request_id: requestId,
                cancelled: true,
                user_id: userId,
            };
            wsRef.current.send(JSON.stringify(cancelResponse));
        }

        // Broadcast close event to all tabs
        if (broadcastRef.current) {
            broadcastRef.current.postMessage({ type: "CLOSE_HUMAN_HELP" });
        }

        setModalOpen(false);
        setInputValue("");
        setMessage("");
        setRequestId("");
    }, [requestId]);

    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && !isSubmitting) {
                handleSubmit();
            }
        },
        [handleSubmit, isSubmitting]
    );

    // Only render modal UI when modal is requested AND user has BOTMANAGER permission
    if (!isModalOpen || !hasBotManagerPermission) {
        return null;
    }

    return (
        <Modal
            isOpen={isModalOpen && hasBotManagerPermission}
            toggle={handleClose}
            backdrop="static"
            keyboard={false}
            size="lg"
        >
            <ModalHeader toggle={handleClose} className="d-flex align-items-center">
                <div className="d-flex align-items-center me-2">
                    <MessageCircle size={24} className="text-primary me-2" />
                    Human Assistance Required
                </div>
            </ModalHeader>
            <ModalBody>
                <div className="text-center mb-4">
                    <div className="d-inline-flex align-items-center justify-content-center p-3 bg-light rounded-circle mb-3">
                        <MessageCircle size={32} className="text-primary" />
                    </div>
                    <h5 className="mb-2">Message from the System</h5>
                    <div className="p-3 bg-light rounded text-start">
                        <p className="mb-0 text-muted" style={{ whiteSpace: 'pre-wrap' }}>{message}</p>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="form-label fw-semibold">Your Response</label>
                    <Input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter your response here..."
                        autoFocus
                    />
                </div>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-center">
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !inputValue.trim()}
                    className="d-flex align-items-center"
                >
                    <Send size={16} className="me-2" />
                    {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default HumanHelp;
